import React, { useEffect, useState } from "react";

import { Icons } from "../../data/Assets";

import { TreeItem } from "../reusables/TreeItem";
import { SVGIcon } from "../wrappers/SVGIcon";

import { IProgramsTreeProps, ITreeItem, IComponentForTree, IProgramForTree, IProgramFunctionForTree } from "../../interfaces/tree";

import { setItemsRecursive, resetSelected, findItemRecursive, removeTemporaryItems } from "../../helpers/tree";

const LAST_LEVEL = 2;
const ProgramsTree = (props: IProgramsTreeProps) => {
    const { programs, selected, setSelected, expanded, setExpanded } = props;

    const [items, setItems] = useState<ITreeItem[]>([]);

    useEffect(() => {
        if (programs.length > 0) {
            setItems(setItemsRecursive({
                type: 'program',
                items: programs,
                onClickItem: onClickItem,
                selected: selected,
                expanded: expanded,
                parentMethod: null,
                lastLevel: LAST_LEVEL,
            }));
        } else {
            setItems([]);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programs]);

    const onClickItem = (item: ITreeItem) => {
        let cExpanded = false;
        setExpanded(prevData => {
            const newData = [...prevData];

            const eItemIdx = newData.findIndex(itm => itm.id === item.id && itm.level === item.level);
            if (eItemIdx === -1) {
                newData.push({
                    id: item.id,
                    level: item.level,
                });
                cExpanded = true;
            } else {
                const itm = findItemRecursive(items, item.id, item.level);
                if (itm?.selected) {
                    newData.splice(eItemIdx, 1);
                    cExpanded = false;
                } else {
                    cExpanded = true;
                };
            };

            return newData;
        });

        setSelected({
            id: item.id,
            level: item.level,
            data: item.data,
        });

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const itm = findItemRecursive(newItems, item.id, item.level);
            if (itm) {
                itm.selected = true;
                itm.expanded = cExpanded;
            };

            return newItems;
        });
    };

    const getDisabled = (type: 'program' | 'component' | 'function') => {
        if (selected?.id === 0) return true;

        if (type === 'program') {
            return false;
        } else if (type === 'component') {
            if (selected) {
                if (selected.level === 0) {
                    return false;
                } else if (selected.level === 1) {
                    return selected.id === 0;
                } else {
                    return true;
                }
            } else {
                return true;
            };
        } else if (type === 'function') {
            if (!selected) return true;

            if (selected.level === 0) {
                return true;
            } else if (selected.level === 1) {
                return selected.id === 0;
            } else if (selected.level === 2) {
                return selected.id === 0;
            } else {
                return false;
            }
        }

        return true;
    };

    useEffect(() => {
        if (!selected || selected.id > 0) {
            setItems(prevState => {
                return removeTemporaryItems([...prevState]);
            });

            setExpanded(prevData => {
                return prevData.filter(item => item.id > 0);
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const onAddNewProgram = () => {
        if (getDisabled('program')) return;

        setItems(prevState => {
            return removeTemporaryItems([...prevState]);
        });

        const data: IProgramForTree = {
            id: 0,
            name: '',
            subtitle: '',
            method: 'and',
            active: true,
            description: '',
            gov_url: '',
            components: [],
            order: 0,
            difficulty: 'easy',
            cost: 'one-star',
            timeframe: '1-3-month',
            date_created: "",
            date_modified: "",
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);
            newItems.unshift({
                type: 'program',
                id: data.id,
                parentId: null,
                level: 0,
                name: "New Program",
                method: data.method,
                parentMethod: null,
                children: [],
                data: data,
                selected: true,
                expanded: false,
                onClickItem: () => { },
                lastLevel: LAST_LEVEL,
            });
            return newItems;
        });
        setSelected({
            id: data.id,
            level: 0,
            data: data,
        });
    };

    const onAddNewComponent = () => {
        if (getDisabled('component') || !selected) return;

        let program_id = 0;
        if (selected.level === 0) {
            const selectedProgram = findItemRecursive(items, selected.id, 0);
            program_id = selectedProgram?.data?.id ?? 0;
        } else if (selected.level === 1) {
            const selectedComponent = findItemRecursive(items, selected.id, 1);
            program_id = (selectedComponent?.data as IComponentForTree | null)?.program_id ?? 0;
        } else {
            return;
        }

        if (!program_id) return;

        const data: IComponentForTree = {
            id: 0,
            program_id: program_id,
            method: 'and',
            description: '',
            program_functions: [],
        };

        const oProgram = findItemRecursive([...items], program_id, 0);
        if (oProgram?.expanded === false) {
            onClickItem(oProgram);
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const program = findItemRecursive(newItems, program_id, 0);
            if (program) {
                program.children.unshift({
                    type: 'program',
                    id: data.id,
                    parentId: program.id,
                    level: 1,
                    name: "New Component",
                    method: data.method,
                    parentMethod: null,
                    children: [],
                    data: data,
                    selected: true,
                    expanded: true,
                    onClickItem: () => { },
                    lastLevel: LAST_LEVEL,
                });
            };
            return newItems;
        });
        setSelected({
            id: data.id,
            level: 1,
            data: data,
        });
    };

    const onAddNewFunction = () => {
        if (getDisabled('function') || !selected) return;

        let component_id = 0;
        if (selected.level === 1) {
            const selectedComponent = findItemRecursive(items, selected.id, 1);
            component_id = selectedComponent?.data?.id ?? 0;
        } else if (selected.level === 2) {
            const selectedFunction = findItemRecursive(items, selected.id, 2);
            component_id = (selectedFunction?.data as IProgramFunctionForTree | null)?.component_id ?? 0;
        } else {
            return;
        }

        if (!component_id) return;

        const data: IProgramFunctionForTree = {
            id: 0,
            component_id: component_id,
            name: '',
            parameters: [],
            description: '',
        };

        const oComponent = findItemRecursive([...items], component_id, 1);
        if (oComponent?.expanded === false) {
            onClickItem(oComponent);
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const component = findItemRecursive(newItems, component_id, 1);
            if (component) {
                component.children.unshift({
                    type: 'program',
                    id: data.id,
                    parentId: component.id,
                    level: 2,
                    name: "New Function",
                    method: null,
                    parentMethod: null,
                    children: [],
                    data: data,
                    selected: true,
                    expanded: true,
                    onClickItem: () => { },
                    lastLevel: LAST_LEVEL,
                });
            };
            return newItems;
        });

        setSelected({
            id: data.id,
            level: 2,
            data: data,
        });
    };

    return (
        <div className="immployer_admin__tree_component">
            <div className="immployer_admin__tree_top">
                <button
                    className={`immployer_admin__tree__action_btn ${getDisabled('program') ? 'immployer_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewProgram}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>Program</span>
                </button>
                <button
                    className={`immployer_admin__tree__action_btn ${getDisabled('component') ? 'immployer_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewComponent}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>Component</span>
                </button>
                <button
                    className={`immployer_admin__tree__action_btn ${getDisabled('function') ? 'immployer_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewFunction}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>Function</span>
                </button>
            </div>
            <div className="immployer_admin__tree_body">
                {items.length > 0
                    ? items.map(item =>
                        <TreeItem
                            type='program'
                            key={item.id}
                            id={item.id}
                            parentId={item.parentId}
                            level={item.level}
                            name={item.name}
                            method={item.method}
                            parentMethod={null}
                            onClickItem={onClickItem}
                            children={item.children}
                            data={item.data}
                            selected={item.selected}
                            expanded={item.expanded}
                            lastLevel={LAST_LEVEL}
                        />
                    )
                    : <div className="immployer_admin__tree__empty">No programs yet.</div>
                }
            </div>
            <div className="immployer_admin__tree_bottom">
                <button className={`immployer_admin__tree__action_btn immployer_admin__tree__action_btn__disabled`}>
                    <SVGIcon src={Icons.ArrowShort} rotation={180} />
                    <span>Move up</span>
                </button>
                <button className={`immployer_admin__tree__action_btn immployer_admin__tree__action_btn__disabled`}>
                    <SVGIcon src={Icons.ArrowShort} />
                    <span>Move down</span>
                </button>
            </div>
        </div>
    );
};

export default ProgramsTree;;